import {
  collection, doc, addDoc, getDoc, getDocs, setDoc,
  deleteDoc, updateDoc, query, where, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { BIBLE_BOOKS, getVersesForChapter } from './bibleData';

// Parses a single comma-separated segment for a Passages Plan, e.g.
// "Ephesians 2:8-10" or "John 3:16" (single verse — no dash needed).
// This is the format that distinguishes Passages Plans from Reading Plans:
// a colon before the verse number is required here, never present in a
// Reading Plan's "Book C" / "Book C-C2" chapter syntax.
export function parsePassageSegment(segment) {
  const trimmed = segment.trim();
  if (!trimmed) return null;
  const match = trimmed.match(/^(.+?)\s+(\d+):(\d+)(?:-(\d+))?$/);
  if (!match) return null;
  const [, bookPart, chapterStr, startStr, endStr] = match;
  const book = BIBLE_BOOKS.find(b => b.name.toLowerCase() === bookPart.trim().toLowerCase());
  if (!book) return null;

  const chapter = parseInt(chapterStr, 10);
  if (chapter < 1 || chapter > book.chapters) return null;

  const versesInChapter = getVersesForChapter(book.id, chapter);
  if (versesInChapter.length === 0) return null;
  const maxVerse = versesInChapter[versesInChapter.length - 1];

  const startVerse = parseInt(startStr, 10);
  const endVerse = endStr ? parseInt(endStr, 10) : startVerse;
  if (startVerse < 1 || endVerse > maxVerse || startVerse > endVerse) return null;

  return { book: book.id, bookName: book.name, chapter, startVerse, endVerse };
}

// Human-readable label for a passage, e.g. "John 3:16" for a single verse or
// "Ephesians 2:8-10" for a range — the inverse of parsePassageSegment.
export function formatPassageLabel(passage) {
  const { bookName, chapter, startVerse, endVerse } = passage;
  const versePart = startVerse === endVerse ? `${startVerse}` : `${startVerse}-${endVerse}`;
  return `${bookName} ${chapter}:${versePart}`;
}

// ── Firestore functions ───────────────────────────────────────────────────
// visibility: 'personal' (only author records) | 'family' (any member can copy+record)
// createdByName is stored so the Listen label "Plan · by Author" works without extra fetches.

export async function createPassagePlan({ name, description, passages, visibility, familyId, createdBy, createdByName }) {
  return addDoc(collection(db, 'passagePlans'), {
    name: name.trim(),
    description: (description || '').trim(),
    passages,
    visibility,
    familyId,
    createdBy,
    createdByName: createdByName || '',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function updatePassagePlan(planId, updates) {
  await updateDoc(doc(db, 'passagePlans', planId), {
    ...updates,
    updatedAt: serverTimestamp(),
  });
}

export async function deletePassagePlan(planId) {
  await deleteDoc(doc(db, 'passagePlans', planId));
}

export async function fetchPassagePlan(planId) {
  const d = await getDoc(doc(db, 'passagePlans', planId));
  if (!d.exists()) return null;
  return { id: d.id, ...d.data() };
}

// Plans this user created (personal + family visibility). Used for My Plans tab.
export async function fetchMyCreatedPassagePlans(uid) {
  if (!uid) return [];
  const snap = await getDocs(
    query(collection(db, 'passagePlans'), where('createdBy', '==', uid))
  );
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
}

// All plans visible to this family's members: every plan from the family
// (all visibility levels) plus any public plans from other families.
export async function fetchFamilyPassagePlans(familyIds) {
  if (!familyIds?.length) return [];
  const [familySnap, publicSnap] = await Promise.all([
    getDocs(query(collection(db, 'passagePlans'), where('familyId', 'in', familyIds.slice(0, 30)))),
    getDocs(query(collection(db, 'passagePlans'), where('visibility', '==', 'public'))),
  ]);
  const seen = new Set();
  const results = [];
  for (const d of [...familySnap.docs, ...publicSnap.docs]) {
    if (!seen.has(d.id)) {
      seen.add(d.id);
      results.push({ id: d.id, ...d.data() });
    }
  }
  return results.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
}

// ── Subscriptions ─────────────────────────────────────────────────────────
// users/{uid}/passagePlanSubscriptions/{planId} — one doc per plan, multiple allowed.

export async function fetchMyPassagePlanSubscriptions(uid) {
  if (!uid) return [];
  const snap = await getDocs(collection(db, `users/${uid}/passagePlanSubscriptions`));
  return snap.docs.map(d => ({ docId: d.id, ...d.data() }));
}

// Subscribe to a plan (idempotent). For a family plan the user is copying, pass
// the original plan so authorId/authorName are captured for the Listen label.
export async function subscribePassagePlan(uid, plan) {
  await setDoc(
    doc(db, `users/${uid}/passagePlanSubscriptions`, plan.id),
    {
      planId: plan.id,
      planName: plan.name || '',
      authorId: plan.createdBy || uid,
      authorName: plan.createdByName || '',
      subscribedAt: serverTimestamp(),
      status: 'active',
      totalPassages: (plan.passages || []).length,
    },
    { merge: true }
  );
  await updateDoc(doc(db, 'users', uid), { lastPassagePlanId: plan.id });
}

export async function unsubscribePassagePlan(uid, planId) {
  await deleteDoc(doc(db, `users/${uid}/passagePlanSubscriptions`, planId));
}

// When a plan is renamed, patch the cached name in the user's subscription doc.
export async function syncPassagePlanSubName(uid, planId, planName) {
  const subRef = doc(db, `users/${uid}/passagePlanSubscriptions`, planId);
  const snap = await getDoc(subRef);
  if (snap.exists()) await updateDoc(subRef, { planName });
}

// ── Per-user progress ─────────────────────────────────────────────────────
// users/{uid}/passagePlanProgress/{planId} — each person's own progress.

export async function writePassagePlanProgress(uid, planId, completedPassageIndexes) {
  if (!uid || !planId) return;
  await setDoc(
    doc(db, `users/${uid}/passagePlanProgress`, planId),
    { completedPassageIndexes, updatedAt: serverTimestamp() },
    { merge: true }
  );
}

export async function fetchPassagePlanProgress(uid, planId) {
  if (!uid || !planId) return null;
  try {
    const d = await getDoc(doc(db, `users/${uid}/passagePlanProgress`, planId));
    return d.exists() ? d.data() : null;
  } catch {
    return null;
  }
}

// The current user's own recordings, in the shape computePassagePlanProgress
// expects ({book, chapter, verse}) — used to compute per-plan progress
// client-side without a Firestore round trip per plan.
export async function fetchUserRecordingsForPassagePlan(uid) {
  if (!uid) return [];
  const snap = await getDocs(query(collection(db, 'recordings'), where('userId', '==', uid)));
  return snap.docs.map(d => d.data());
}

// Compute which passages the current user has fully recorded.
// Returns { completed, total, pct, completedIndexes, nextPassage }.
export function computePassagePlanProgress(passages, userRecordings) {
  if (!passages?.length) {
    return { completed: 0, total: 0, pct: 0, completedIndexes: [], nextPassage: null, recordedVerses: 0, totalVerses: 0 };
  }
  const recordedKeys = new Set();
  for (const r of userRecordings) {
    if (r.book && r.chapter && r.verse) {
      recordedKeys.add(`${r.book}_${Number(r.chapter)}_${Number(r.verse)}`);
    }
  }
  const completedIndexes = [];
  let totalVerses = 0;   // total verses across every passage in the plan
  let recordedVerses = 0; // how many the user has recorded (verse-level)
  for (let i = 0; i < passages.length; i++) {
    const p = passages[i];
    let allDone = true;
    for (let v = Number(p.startVerse); v <= Number(p.endVerse); v++) {
      totalVerses++;
      if (recordedKeys.has(`${p.book}_${Number(p.chapter)}_${v}`)) recordedVerses++;
      else allDone = false;
    }
    if (allDone) completedIndexes.push(i);
  }
  const doneSet = new Set(completedIndexes);
  const nextPassage = passages.find((_, i) => !doneSet.has(i)) ?? null;
  const completed = completedIndexes.length;
  const total = passages.length;
  return { completed, total, pct: total > 0 ? Math.round((completed / total) * 100) : 0, completedIndexes, nextPassage, recordedVerses, totalVerses };
}
